import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { MaterialIcon } from "@/components/MaterialIcon";
import { useAuth } from "@/context/AuthContext";
import { linkCls } from "@/components/admin/ui";

const TAB = (to, label, icon) => ({ to: `/admin/${to}`, label, icon, testId: `admin-tab-${to}` });

const GROUP = (name, icon, items) => ({ name, icon, items });

const CONTENT = {  videos: TAB("videos", "Videos", "movie"),
  festivals: TAB("festivals", "Festivals", "festival"),
  categories: TAB("categories", "Categories", "category"),
  styles: TAB("dance-styles", "Dance Styles", "music_note"),
};
const HOME = { to: "/admin", label: "Home", icon: "home", testId: "admin-tab-home", end: true };
const DESIGN = TAB("design", "Design", "palette");
const SETTINGS = TAB("settings", "Settings", "settings");

const ADMIN_NAV = [
  HOME,
  GROUP("Content", "folder", [CONTENT.videos, CONTENT.festivals, CONTENT.categories, CONTENT.styles]),
  DESIGN,
  TAB("sales", "Sales", "payments"),
  TAB("customers", "Customers", "person"),
  TAB("payouts", "Payouts", "account_balance"),
  TAB("users", "Users", "group"),
];

const ORGANIZER_NAV = [
  HOME,
  GROUP("Content", "folder", [CONTENT.videos, CONTENT.festivals]),
  TAB("sales", "Sales", "payments"),
  TAB("customers", "Customers", "person"),
  TAB("payouts", "Payouts", "account_balance"),
];

const rowCls = (active) =>
  `flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[14px] transition-colors ${
    active ? "bg-[#EEF1F6] font-semibold text-[#111]" : "font-medium text-[#374151] hover:bg-[#F6F7F9]"
  }`;

const Item = ({ tab, onNavigate, idSuffix, nested }) => (
  <NavLink
    to={tab.to}
    end={tab.end}
    onClick={onNavigate}
    data-testid={`${tab.testId}${idSuffix}`}
    className={({ isActive }) => `${rowCls(isActive)} ${nested ? "pl-9 pr-3" : ""}`}
  >
    {({ isActive }) => (
      <>
        {!nested && (
          <MaterialIcon
            name={tab.icon}
            filled={isActive}
            size={20}
            weight={400}
            className={`shrink-0 ${isActive ? "text-[#111]" : "text-[#4B5563]"}`}
          />
        )}
        <span className="truncate">{tab.label}</span>
      </>
    )}
  </NavLink>
);

const Group = ({ group, onNavigate, idSuffix, pathname }) => {
  const hasActive = group.items.some((t) => pathname.startsWith(t.to));
  const [open, setOpen] = useState(hasActive);
  const expanded = open;
  return (
    <div data-testid={`admin-group-${group.name.toLowerCase()}${idSuffix}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={rowCls(false)}
        aria-expanded={expanded}
        data-testid={`admin-group-toggle-${group.name.toLowerCase()}${idSuffix}`}
      >
        <MaterialIcon name={group.icon} filled={hasActive} size={20} weight={400} className={`shrink-0 ${hasActive ? "text-[#111]" : "text-[#4B5563]"}`} />
        <span className="flex-1 text-left">{group.name}</span>
        <MaterialIcon
          name="keyboard_arrow_down"
          size={20}
          weight={500}
          className={`shrink-0 text-[#9CA3AF] transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="mt-0.5 space-y-0.5">
          {group.items.map((t) => (
            <Item key={t.to} tab={t} onNavigate={onNavigate} idSuffix={idSuffix} nested />
          ))}
        </div>
      )}
    </div>
  );
};

const AccountBar = ({ user, onNavigate, idSuffix, onLogout }) => {
  const [open, setOpen] = useState(false);
  const label = user.name || user.organizer_for || user.email.split("@")[0];
  const initials = label
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  const itemCls = "block w-full px-4 py-2 text-left text-[14px] font-medium text-[#374151] hover:bg-[#F6F7F9] transition-colors";
  return (
    <div className="relative px-3 pt-3" data-testid={`admin-account-bar${idSuffix}`}>
      {open && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
      {open && (
        <div
          className="absolute bottom-[calc(100%-4px)] left-3 right-3 z-20 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white py-1.5 shadow-[0_12px_30px_rgba(0,0,0,0.12)] df-panel-in"
          data-testid={`admin-account-menu${idSuffix}`}
        >
          <div className="border-b border-[#F3F4F6] px-4 pb-2 pt-1">
            <p className="truncate text-[14px] font-semibold">{label}</p>
            <p className="truncate text-[12px] text-[#6B7280]">{user.email}</p>
          </div>
          <Link
            to="/admin/account"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
            className={itemCls}
            data-testid={`admin-account-link${idSuffix}`}
          >
            Account
          </Link>
          <button
            type="button"
            className={`${itemCls} flex items-center justify-between`}
            onClick={() => {
              setOpen(false);
              toast("Billing is coming soon");
            }}
            data-testid={`admin-billing-link${idSuffix}`}
          >
            Billing
            <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-semibold text-[#6B7280]">Soon</span>
          </button>
          <button type="button" className={`${itemCls} border-t border-[#F3F4F6] mt-1 pt-2`} onClick={onLogout} data-testid={`admin-logout-button${idSuffix}`}>
            Log out
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors ${open ? "bg-[#EEF1F6]" : "hover:bg-[#F6F7F9]"}`}
        aria-expanded={open}
        data-testid={`admin-account-button${idSuffix}`}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FDE8E8] text-[12px] font-bold text-[#B45309]">{initials}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-[#111]">{label}</span>
          <span className="block truncate text-[12px] text-[#6B7280]" data-testid={`admin-user-email${idSuffix}`}>{user.email}</span>
        </span>
        <MaterialIcon name="unfold_more" size={18} weight={500} className="shrink-0 text-[#9CA3AF]" />
      </button>
    </div>
  );
};

const Nav = ({ items, user, onLogout, onNavigate, idSuffix = "" }) => {
  const { pathname } = useLocation();
  return (
    <nav className="flex h-full min-h-0 flex-1 flex-col px-3">
      <div className="space-y-1 overflow-y-auto">
        {items.map((i) =>
          i.items ? (
            <Group key={i.name} group={i} onNavigate={onNavigate} idSuffix={idSuffix} pathname={pathname} />
          ) : (
            <Item key={i.to} tab={i} onNavigate={onNavigate} idSuffix={idSuffix} />
          ),
        )}
      </div>
      <div className="mt-auto border-t border-[#E5E7EB] pt-3">
        <Link to="/" onClick={onNavigate} className={rowCls(false)} data-testid={`admin-sidebar-view-site${idSuffix}`}>
          <MaterialIcon name="open_in_new" size={20} weight={400} className="shrink-0 text-[#4B5563]" />
          <span>View site</span>
        </Link>
        <Item tab={SETTINGS} onNavigate={onNavigate} idSuffix={idSuffix} />
      </div>
      <div className="-mx-3 border-t border-[#E5E7EB] pb-1">
        <AccountBar user={user} onNavigate={onNavigate} idSuffix={idSuffix} onLogout={onLogout} />
      </div>
    </nav>
  );
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  const admin = user.role === "admin";
  if (!admin && !user.onboarded) return <Navigate to="/admin/onboarding" replace />;
  const items = admin ? ADMIN_NAV : ORGANIZER_NAV;
  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-white text-[#111]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-[#E5E7EB] bg-white px-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            className="lg:hidden flex items-center text-[#374151]"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            data-testid="admin-menu-button"
          >
            <MaterialIcon name="menu" size={24} />
          </button>
          <Link to="/" data-testid="admin-logo-link">
            <Logo size={22} />
          </Link>
          <span className="hidden sm:block border-l border-[#E5E7EB] pl-3 text-[13px] text-[#6B7280] truncate" data-testid="admin-role-label">
            {admin ? "Admin" : user.organizer_for || "Organizer"}
          </span>
        </div>
        <div className="flex items-center gap-3 sm:gap-5">
          <Link to="/" className={`hidden md:inline text-[13px] ${linkCls}`} data-testid="admin-view-site-link">
            View site
          </Link>
        </div>
      </header>
      <div className="flex">
        <aside className="hidden lg:flex lg:flex-col sticky top-14 h-[calc(100vh-56px)] w-[236px] shrink-0 border-r border-[#E5E7EB] py-4" data-testid="admin-sidebar">
          <Nav items={items} user={user} onLogout={handleLogout} />
        </aside>
        {menuOpen && (
          <div className="fixed inset-0 z-40 lg:hidden" data-testid="admin-mobile-menu">
            <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
            <aside className="absolute left-0 top-0 flex h-full w-[262px] flex-col bg-white py-4 shadow-xl df-panel-in overflow-y-auto">
              <div className="mb-3 flex items-center justify-between px-5">
                <Logo size={20} />
                <button type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="admin-menu-close">
                  <MaterialIcon name="close" size={22} className="text-[#6B7280]" />
                </button>
              </div>
              <Nav items={items} user={user} onLogout={handleLogout} onNavigate={() => setMenuOpen(false)} idSuffix="-mobile" />
            </aside>
          </div>
        )}
        <main className="min-w-0 flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          <div className="mx-auto max-w-[1180px] 2xl:max-w-[1440px] 3xl:max-w-[1700px] 4xl:max-w-[2100px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export const AdminOnly = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user?.role !== "admin") return <Navigate to="/admin/festivals" replace />;
  return children;
};
